import {Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors} from '@angular/forms';
import {FinanceRelanceRecord} from '../../services/finance-relance/finance-relance-record.model';
import {FinanceRelanceRecordSection} from '../../services/finance-relance/finance-relance-record-section.enum';
import {FinanceRelanceRecordType} from '../../services/finance-relance/finance-relance-record-type.enum';
import {convertPriceToNumber} from '../../utils/convert-price-to-number.util';

@Component({
	selector: 'tvsub-financial-table',
	templateUrl: './financial-table.component.html',
	styleUrls: ['./financial-table.component.scss'],
	standalone: false
})
export class FinancialTableComponent implements OnInit {
	records = input<FinanceRelanceRecord[]>([]);
	section = input.required<FinanceRelanceRecordSection>();
	type = input.required<FinanceRelanceRecordType>();
	decimals = input<boolean>(true);
	disableAdd = input<boolean>(false);

	showErrors = signal(false);
	visibleRecords = computed(() => {
		const section = this.section();
		const type = this.type();
		return this.records().filter(r => r.type === type && r.section === section);
	});

	form: FormGroup;
	#fb = inject(FormBuilder);

	get recordsForm(): FormArray {
		return this.form.get('records') as FormArray;
	}

	ngOnInit() {
		let records = this.visibleRecords();
		if (records?.length === 0) {
			records = [{ amount: 0, description: '', section: this.section(), type: this.type() }];
		}

		this.form = this.#fb.group({
			records: this.#fb.array(records.map(r => this.createLineGroup(r))),
		});
	}

	addLine() {
		this.recordsForm.push(this.createLineGroup());
	}

	removeLine(index: number) {
		this.recordsForm.removeAt(index);
	}

	isFormValid(): boolean {
		this.showErrors.set(true);
		return this.form.valid;
	}

	getData(): FinanceRelanceRecord[] {
		return this.recordsForm.getRawValue().map((sm: any) => {
			return {
				...sm,
				fid: undefined,
			};
		});
	}

	private createLineGroup(record?: FinanceRelanceRecord): FormGroup {
		const fid = Math.floor(Math.random() * 999_999);

		const group = this.#fb.group({
			fid: [fid],
			id: [record?.id],
			amount: [record?.amount || null],
			description: [record?.description],
			section: [record?.section || this.section()],
			type: [record?.type || this.type()],
		});

		group.get('amount')?.setValidators(this.validateIfSame(group, 'description', 'outer'));
		group.get('description')?.setValidators(this.validateIfSame(group, 'amount', 'this'));

		if (record?.disableDescription) {
			group.get('description')?.disable();
		}

		return group;
	}

	private validateIfSame(group: FormGroup, key: string, isNumber?: 'this' | 'outer'): any {
		return (fg: AbstractControl): ValidationErrors | null => {
			const ctrl = group.get(key);

			if (ctrl) {
				setTimeout(() => {
					ctrl.updateValueAndValidity();
				}, 40);

				const v1 = isNumber === 'this' ? convertPriceToNumber(ctrl.value) : ctrl.value;
				const v2 = isNumber === 'outer' ? convertPriceToNumber(fg.value) : fg.value;

				if (!!v1 === !!v2) {
					return null;
				}
			}

			return {
				required: true
			};
		};
	}
}
